import React from "react";
import ReactDOM, { createRoot, Root } from "react-dom/client";
import "./index.css";
import App from "./App";
import { createClient } from "graphql-ws";
import reportWebVitals from "./reportWebVitals";
import { getMainDefinition } from "@apollo/client/utilities";
import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { ConsultationProvider } from "./contexts/consultationContext";
import {
  ApolloClient,
  InMemoryCache,
  HttpLink,
  ApolloLink,
  CombinedGraphQLErrors,
  ServerError,
} from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { ErrorLink } from "@apollo/client/link/error";
import * as Sentry from "@sentry/react";
import { NotificationSettingsProvider } from "./contexts/notificationSettingsContext";
import {
  ErrorBoundary,
  handleGlobalError,
} from "./components/errors/ErrorBoundary";
import { ErrorFallbackUI } from "./components/errors/router-error-boundaries/ErrorFallbackUI";
import { getConsultationIdFromPath } from "./utils/funcs";
import { sessionStore } from "./utils/sessionStore";
import { logException } from "./libs/sentry";
import { captureCampaignId } from "./libs/mixpanel";
import {
  initPostHog,
  phCapture,
  phCaptureException,
  PH,
  posthog,
} from "./libs/posthog";
import { PostHogProvider } from "@posthog/react";

const apiBaseUrl = import.meta.env.VITE_API_BASE_URL;

let root: Root | null = null;
let client: ApolloClient | null = null;

try {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    integrations: [Sentry.browserTracingIntegration()],
    environment: import.meta.env.VITE_SENTRY_ENV,
    tracesSampleRate: 0.2,
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0,
  });

  // Isolated from the surrounding try/catch's real purpose: this block wraps
  // the entire app bootstrap, and its catch renders a full "Setup failed"
  // screen for every user. Mixpanel capture must never be able to trigger
  // that, so it gets its own local guard rather than relying on
  // captureCampaignId's internal safety net.
  try {
    captureCampaignId();
  } catch (error) {
    console.warn("[Mixpanel] Failed to capture campaign-id", error);
  }
  initPostHog();

  let emergencyRoot: Root | null = null;

  const showEmergencyError = (error: Error) => {
    let container = document.getElementById("emergency-error-root");

    if (!container) {
      container = document.createElement("div");
      container.id = "emergency-error-root";
      container.style.cssText = `
      position: fixed;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      z-index: 999999;
      background: white;
    `;
      document.body.appendChild(container);
    }

    if (!emergencyRoot) {
      emergencyRoot = createRoot(container);
    }

    emergencyRoot.render(<ErrorFallbackUI error={error} isEmergency={true} />);
  };

  const safeHandleGlobalError = (error: Error, errorInfo?: React.ErrorInfo) => {
    handleGlobalError(error, errorInfo);

    if (!ErrorBoundary.instance) {
      console.error("CRITICAL: Error before ErrorBoundary mounted", error);
      showEmergencyError(error);
    }
  };

  const isStaleChunkError = (error: Error): boolean => {
    const msg = (error?.message || "").toLowerCase();
    return (
      msg.includes("not a valid javascript mime type") ||
      msg.includes("failed to fetch dynamically imported") ||
      msg.includes("importing a module script failed") ||
      msg.includes("unable to preload css")
    );
  };

  const isNonFatalError = (error: Error): boolean => {
    const msg = (error?.message || "").toLowerCase();
    const name = error?.name || "";
    return (
      name === "FirebaseError" ||
      msg.includes("failed to fetch") ||
      msg.includes("network request failed") ||
      msg.includes("networkerror") ||
      msg.includes("the client is offline") ||
      msg.includes("webchannel") ||
      msg.includes("transport errored") ||
      msg.includes("could not reach cloud firestore") ||
      msg.includes("offline") ||
      // Ad blockers generate "Script error." for cross-origin blocked scripts
      msg === "script error." ||
      msg === "script error"
    );
  };

  const getGlobalErrorContext = () => {
    const path = window.location.pathname;
    const consultationId = getConsultationIdFromPath(path) || "UNKNOWN";
    const actor = path.includes("/doctor") ? "doctor" : "user";
    return { consultationId, actor } as const;
  };

  window.addEventListener("error", (event) => {
    event.preventDefault();

    const error = event.error || new Error(event.message || "Unknown error");
    const { consultationId, actor } = getGlobalErrorContext();

    if (isStaleChunkError(error)) {
      const alreadyReloaded =
        sessionStorage.getItem("stale_chunk_reload") === "1";
      if (!alreadyReloaded) {
        sessionStorage.setItem("stale_chunk_reload", "1");
        window.location.reload();
        return;
      }
      sessionStorage.removeItem("stale_chunk_reload");
      // reload didn't fix it — must return explicitly here.
      // "failed to fetch dynamically imported" also matches isNonFatalError below,
      // which would swallow the error and leave a permanently blank page.
      logException(
        error,
        consultationId,
        actor,
        "stale-chunk-reload-failed",
        "window.error",
      );
      phCapture(PH.CHUNK_LOAD_ERROR, {
        consultationId,
        actor,
        reloadAttempted: true,
        source: "window.error",
      });
      safeHandleGlobalError(error, { componentStack: error.stack || "" });
      return;
    }

    if (isNonFatalError(error)) {
      console.warn("Non-fatal error suppressed from UI:", error.message);
      logException(
        error,
        consultationId,
        actor,
        "non-fatal-global-error",
        "window.error",
      );
      phCaptureException(error, {
        consultationId,
        actor,
        action: "non-fatal-global-error",
        source: "window.error",
      });
      return;
    }

    console.error("Global error caught:", error);
    logException(error, consultationId, actor, "global-error", "window.error");
    phCaptureException(error, {
      consultationId,
      actor,
      action: "global-error",
      source: "window.error",
    });

    safeHandleGlobalError(error, {
      componentStack: error.stack || "",
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    event.preventDefault();

    const error =
      event.reason instanceof Error
        ? event.reason
        : new Error(String(event.reason || "Unhandled promise rejection"));
    const { consultationId, actor } = getGlobalErrorContext();

    if (isStaleChunkError(error)) {
      const alreadyReloaded =
        sessionStorage.getItem("stale_chunk_reload") === "1";
      if (!alreadyReloaded) {
        sessionStorage.setItem("stale_chunk_reload", "1");
        window.location.reload();
        return;
      }
      sessionStorage.removeItem("stale_chunk_reload");
      logException(
        error,
        consultationId,
        actor,
        "stale-chunk-reload-failed",
        "window.unhandledrejection",
      );
      phCapture(PH.CHUNK_LOAD_ERROR, {
        consultationId,
        actor,
        reloadAttempted: true,
        source: "window.unhandledrejection",
      });
      safeHandleGlobalError(error, { componentStack: error.stack || "" });
      return;
    }

    if (isNonFatalError(error)) {
      console.warn("Non-fatal rejection suppressed from UI:", error.message);
      logException(
        error,
        consultationId,
        actor,
        "non-fatal-unhandled-rejection",
        "window.unhandledrejection",
      );
      phCaptureException(error, {
        consultationId,
        actor,
        action: "non-fatal-unhandled-rejection",
        source: "window.unhandledrejection",
      });
      return;
    }

    console.error("Unhandled rejection caught:", error);
    logException(
      error,
      consultationId,
      actor,
      "unhandled-rejection",
      "window.unhandledrejection",
    );
    phCaptureException(error, {
      consultationId,
      actor,
      action: "unhandled-rejection",
      source: "window.unhandledrejection",
    });

    safeHandleGlobalError(error, {
      componentStack: error.stack || "",
    });
  });

  // Tells index.html's pre-boot error listeners to stand down — this handler now
  // reports every error with richer context, so both firing would double-report to Sentry.
  (
    window as Window & { _richErrorHandlerReady?: boolean }
  )._richErrorHandlerReady = true;

  root = ReactDOM.createRoot(document.getElementById("root") as HTMLElement);

  const wsLink = new GraphQLWsLink(
    createClient({
      url: `${apiBaseUrl}`,
    }),
  );

  const httpLink = new HttpLink({
    uri: apiBaseUrl,
  });

  // Prevents simultaneous failing queries from triggering multiple redirects.
  let handlingAuthError = false;

  const redirectToRejoin = () => {
    // Already on the rejoin page — the waiting room's own init error handling
    // takes over (retries → initFailed UI). Don't redirect again.
    if (window.location.search.includes("err_rejoin=1")) return;

    const currentPath = window.location.pathname;
    const consultationId = getConsultationIdFromPath(currentPath);
    const isDoctor = currentPath.includes("/doctor");
    const rejoinPath = isDoctor
      ? `/doctor/${consultationId}?err_rejoin=1`
      : `/${consultationId}?err_rejoin=1`;

    window.location.href = rejoinPath;
  };

  // Authentication error handler
  const errorLink = new ErrorLink(({ error }) => {
    if (handlingAuthError) return;

    if (CombinedGraphQLErrors.is(error)) {
      const hasAuthError = error.errors.some(
        ({ message, extensions }) =>
          extensions?.code === "UNAUTHENTICATED" ||
          extensions?.code === "UNAUTHORIZED" ||
          message.toLowerCase().includes("authentication") ||
          message.toLowerCase().includes("unauthorized") ||
          message.toLowerCase().includes("not authenticated"),
      );

      if (hasAuthError) {
        handlingAuthError = true;
        setTimeout(() => {
          handlingAuthError = false;
        }, 500);

        const consultationId =
          getConsultationIdFromPath(window.location.pathname) ?? undefined;
        console.error("Authentication error detected");
        sessionStore.clearAuth(consultationId);
        redirectToRejoin();
      }
    } else if (error instanceof ServerError && error.statusCode === 401) {
      handlingAuthError = true;
      setTimeout(() => {
        handlingAuthError = false;
      }, 500);

      const consultationId =
        getConsultationIdFromPath(window.location.pathname) ?? undefined;
      console.error("Network authentication error (401)");
      sessionStore.clearAuth(consultationId);
      redirectToRejoin();
    }
  });

  const splitLink = ApolloLink.split(
    ({ query }) => {
      const definition = getMainDefinition(query);
      return (
        definition.kind === "OperationDefinition" &&
        definition.operation === "subscription"
      );
    },
    wsLink,
    httpLink,
  );

  const authMiddleware = new ApolloLink((operation, forward) => {
    const consultationId =
      getConsultationIdFromPath(window.location.pathname) ?? "";
    const accessToken = sessionStore.getAccessToken(consultationId);
    const auth = accessToken ? { authorization: `bearer ${accessToken}` } : {};
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    operation.setContext((context: any) => ({
      headers: {
        ...context.headers,
        ...auth,
        "Access-Control-Allow-Credentials": true,
        "Content-Type": "application/json",
      },
    }));

    return forward(operation);
  });

  client = new ApolloClient({
    devtools: { enabled: true },
    cache: new InMemoryCache({
      typePolicies: {
        PharmacyDrug: {
          keyFields: false,
        },
      },
    }),
    link: ApolloLink.from([authMiddleware, errorLink, splitLink]),
  });
} catch (error) {
  document.getElementById("app-loading")?.remove();

  const errMsg =
    error instanceof Error ? error.message : String(error ?? "unknown error");
  const errName = error instanceof Error ? error.name || "Error" : "Error";

  // Sentry.init() itself may be what failed, so report via index.html's
  // dependency-free reporter rather than the Sentry SDK.
  const reportPreBootError = (
    window as Window & {
      _reportPreBootError?: (message: string, kind: string) => void;
    }
  )._reportPreBootError;
  reportPreBootError?.(`${errName}: ${errMsg}`, "setup-failure");

  const container = document.getElementById("root");
  if (container) {
    const outer = document.createElement("div");
    outer.style.cssText =
      "position:fixed;top:0;left:0;width:100%;height:100%;display:flex;align-items:center;justify-content:center;background:#fff;font-family:sans-serif;padding:20px;box-sizing:border-box;";
    const inner = document.createElement("div");
    inner.style.cssText = "text-align:center;max-width:400px;";
    const title = document.createElement("p");
    title.style.cssText =
      "font-size:14px;color:#111827;font-weight:600;margin:0 0 8px;";
    title.textContent = `Setup failed: ${errName}`;
    const msg = document.createElement("p");
    msg.style.cssText =
      "font-size:12px;color:#6b7280;word-break:break-all;margin:0;";
    msg.textContent = errMsg;
    inner.appendChild(title);
    inner.appendChild(msg);
    outer.appendChild(inner);
    container.appendChild(outer);
  }
}

if (root && client) {
  root.render(
    <React.StrictMode>
      <PostHogProvider client={posthog}>
        <ErrorBoundary>
          <NotificationSettingsProvider>
            <ApolloProvider client={client}>
              <ConsultationProvider>
                <App />
              </ConsultationProvider>
            </ApolloProvider>
          </NotificationSettingsProvider>
        </ErrorBoundary>
      </PostHogProvider>
    </React.StrictMode>,
  );
  reportWebVitals();
}
